import { useMemo, type CSSProperties } from "react";
import { AttachmentImage } from "@/components/ui/attachment-media";
import type { CardPreviewProps } from "@/components/shapes/card-previews";
import { PreviewTitle } from "@/components/shapes/shared/card-preview-parts";
import { TintBlock } from "@/components/shapes/shared/card-visual-parts";
import { cn } from "@/lib/cn";
import { htmlCardShape, type HtmlCardShape } from "./html-card-shape";
import { usePosterTheme } from "./html-poster-theme";

/** The page's own colour mixed into the card paper (mockup `.blk`). */
function pageTint(tint: string): CSSProperties {
  return {
    "--tint-a": `color-mix(in srgb, ${tint} 52%, var(--card-surface))`,
    "--tint-b": `color-mix(in srgb, ${tint} 26%, var(--card-surface))`,
  } as CSSProperties;
}

/** The poster's place while it is missing: the page's colour, its first line. */
function HtmlPlaceholder({
  seed,
  shape,
  compact,
}: {
  seed: string;
  shape: Extract<HtmlCardShape, { kind: "placeholder" }>;
  compact: boolean;
}) {
  return (
    <TintBlock
      seed={seed}
      className={cn("min-h-0 overflow-hidden p-[1.5em]", compact && "flex-1")}
      style={{
        ...(shape.tint ? pageTint(shape.tint) : null),
        ...(compact ? null : { aspectRatio: shape.ratio }),
      }}
      data-testid="html-card-placeholder"
    >
      {shape.line && (
        <p
          className="line-clamp-3 text-[0.84em] leading-[1.45] opacity-70"
          data-testid="card-preview-excerpt"
        >
          {shape.line}
        </p>
      )}
    </TintBlock>
  );
}

/**
 * An HTML page: its title (mockup `.html .ht`), then its poster for the
 * current theme edge to edge, top crop at the page's own ratio, over its
 * average colour while it loads; a page without a poster yet holds the
 * poster's place in its own colour with its first line.
 */
export function HtmlCardPreview({ frame, density }: CardPreviewProps) {
  const theme = usePosterTheme();
  const shape = useMemo(() => htmlCardShape(frame, theme), [frame, theme]);
  const compact = density === "compact";
  return (
    <div className={cn("flex min-w-0 flex-col", compact && "h-full")}>
      <div className="shrink-0 px-[1.6em] pb-[0.3em] pt-[1.3em]">
        <PreviewTitle>{shape.title}</PreviewTitle>
      </div>
      {shape.kind === "placeholder" ? (
        <HtmlPlaceholder seed={frame.frameId} shape={shape} compact={compact} />
      ) : (
        <AttachmentImage
          src={shape.url}
          alt=""
          className={cn(
            "block w-full object-cover object-top",
            compact && "min-h-0 flex-1",
          )}
          style={{
            aspectRatio: compact ? undefined : shape.ratio,
            backgroundColor: shape.placeholder ?? undefined,
          }}
          data-testid="html-card-poster"
          draggable={false}
        />
      )}
    </div>
  );
}
